import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import Layout from "@/components/Layout";
import { ArrowLeft, ExternalLink, MapPin, Mail, Phone } from "lucide-react";

/**
 * Public Supplier Profile — auto-populated from Prime Supplier registration.
 * No manual updates required. Only fields explicitly submitted at registration
 * are exposed. Route: /supplier/:id
 */

const INDUSTRY_LABELS = {
  community_partner: "Community Partner",
  construction: "Construction",
  resort_operations: "Resort Operations",
  security_risk: "Security / Risk Management",
  information_technology: "Information Technology",
  staffing: "Staffing",
  marketing: "Marketing",
  da_support: "Display America Support",
};

export default function SupplierProfile() {
  const { id } = useParams();
  const [supplier, setSupplier] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/suppliers/${id}`)
      .then(({ data }) => setSupplier(data.supplier))
      .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "error"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return <Layout><section className="container-summit py-24"><div className="overline">Loading supplier…</div></section></Layout>;
  }

  if (error || !supplier) {
    return (
      <Layout>
        <section className="container-summit py-24 max-w-xl">
          <h1 className="serif text-3xl">Supplier profile unavailable</h1>
          <p className="mt-3 text-zinc-500">This supplier hasn&apos;t published a public profile yet.</p>
          <Link to="/venue" className="btn-black mt-8" data-testid="sp-back-to-showcase">
            Back to Supplier Showcase
          </Link>
        </section>
      </Layout>
    );
  }

  const industryLabel = INDUSTRY_LABELS[supplier.industry_category] || supplier.industry || "—";
  const websiteHref = supplier.website && (/^https?:\/\//.test(supplier.website) ? supplier.website : `https://${supplier.website}`);
  const isDA = !!supplier.is_da_support;

  return (
    <Layout>
      {/* Hero */}
      <section className={isDA ? "bg-[#0A0A0A] text-white border-b border-[#A39171]/40" : "bg-[#0A0A0A] text-white border-b border-white/10"}>
        <div className="container-summit py-14 md:py-20">
          <Link to="/venue" className="text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-white inline-flex items-center gap-2"
                data-testid="sp-back">
            <ArrowLeft size={12} /> Supplier Showcase
          </Link>

          <div className="mt-8 grid md:grid-cols-[auto_1fr] gap-8 items-start">
            {supplier.logo_file_id ? (
              <div className="h-24 bg-white flex items-center justify-center p-4" style={{ minWidth: 160 }}>
                <img src={`${BACKEND_URL}/api/files/${supplier.logo_file_id}`}
                     alt={supplier.company_name} className="max-h-16 max-w-[140px] object-contain" />
              </div>
            ) : isDA ? (
              <div className="h-24 bg-white flex items-center justify-center p-4" style={{ minWidth: 200 }}
                   data-testid="sp-da-logo">
                <img src="/brand/display-america-logo.png"
                     alt="Display America"
                     className="max-h-16 max-w-[200px] object-contain" />
              </div>
            ) : (
              <div className="h-24 w-40 border border-white/20 flex items-center justify-center text-white/40 text-[10px] uppercase tracking-[0.22em]">
                No Logo
              </div>
            )}
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#C6B99A]">
                {isDA ? "Onsite Support" : "Prime Supplier"}
              </div>
              <h1 className="serif text-4xl sm:text-5xl md:text-6xl mt-3 leading-[1.05]" data-testid="sp-company-name">
                {supplier.company_name}
              </h1>
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-[11px] uppercase tracking-[0.2em] text-white/70">
                <span data-testid="sp-industry">{industryLabel}</span>
                {supplier.booth_number && (
                  <span className="inline-flex items-center gap-1" data-testid="sp-station">
                    <MapPin size={11} /> Station {supplier.booth_number}
                  </span>
                )}
                {websiteHref && (
                  <a href={websiteHref} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 text-[#C6B99A] hover:text-white"
                     data-testid="sp-website">
                    Website <ExternalLink size={11} />
                  </a>
                )}
              </div>

              {isDA && (
                <div className="mt-8 flex flex-wrap gap-3" data-testid="sp-da-cta">
                  <a href={`mailto:${supplier.email || "info@displayamerica.com"}`}
                     className="inline-flex items-center gap-2 bg-[#A39171] text-black px-5 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-[#C6B99A] transition-colors"
                     data-testid="sp-da-email">
                    <Mail size={12} /> Email the DA team
                  </a>
                  {supplier.phone && (
                    <a href={`tel:${supplier.phone}`}
                       className="inline-flex items-center gap-2 border border-white/40 text-white px-5 py-3 text-[11px] uppercase tracking-[0.22em] hover:bg-white hover:text-black transition-colors"
                       data-testid="sp-da-phone">
                      <Phone size={12} /> Call {supplier.phone}
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="container-summit py-14 md:py-20 max-w-4xl">
        {supplier.description && (
          <Block label="About" testId="sp-about">
            <p className="text-[15px] text-zinc-700 leading-relaxed whitespace-pre-line">{supplier.description}</p>
          </Block>
        )}

        {supplier.products_services && (
          <Block label="Products & Services" testId="sp-products">
            <p className="text-[15px] text-zinc-700 leading-relaxed whitespace-pre-line">{supplier.products_services}</p>
          </Block>
        )}

        {supplier.business_areas?.length > 0 && (
          <Block label="Product & Service Categories" testId="sp-business-areas">
            <div className="flex flex-wrap gap-2">
              {supplier.business_areas.map((a) => (
                <span key={a} className="text-[11px] uppercase tracking-[0.18em] border border-black px-3 py-1.5">
                  {a}
                </span>
              ))}
            </div>
          </Block>
        )}

        {supplier.matchmaking_preferences?.length > 0 && (
          <Block label="Business Matchmaking Interests" testId="sp-matchmaking">
            <div className="text-xs text-zinc-500 mb-3">
              Products or services this supplier is exploring during Business Matchmaking.
            </div>
            <div className="flex flex-wrap gap-2">
              {supplier.matchmaking_preferences.map((a) => (
                <span key={a} className="text-[11px] uppercase tracking-[0.18em] border border-[#A39171] text-[#7A6A4A] px-3 py-1.5">
                  {a}
                </span>
              ))}
            </div>
          </Block>
        )}

        {isDA && supplier.da_support_hours?.length > 0 && (
          <Block label="When we're onsite" testId="sp-da-hours">
            <ul className="text-[15px] text-zinc-700 leading-relaxed space-y-2">
              {supplier.da_support_hours.map((h, i) => (
                <li key={i} className="flex flex-wrap gap-x-4">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 min-w-[220px] pt-1">{h.day}</span>
                  <span>{h.hours}</span>
                </li>
              ))}
            </ul>
          </Block>
        )}

        {(supplier.primary_contact || supplier.contact_title) && (
          <Block label="Primary Contact" testId="sp-contact">
            <div className="text-[15px] text-black">{supplier.primary_contact || "—"}</div>
            {supplier.contact_title && (
              <div className="text-sm text-zinc-500 mt-0.5">{supplier.contact_title}</div>
            )}
          </Block>
        )}

        <div className="border-t border-zinc-200 mt-12 pt-6 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
          {isDA ? "Onsite Support Station · Display America" : "Profile populated from Prime Supplier registration."}
        </div>
      </section>
    </Layout>
  );
}

function Block({ label, children, testId }) {
  return (
    <section className="border-t border-zinc-200 py-8" data-testid={testId}>
      <div className="overline mb-4"><span className="accent-rule" />{label}</div>
      {children}
    </section>
  );
}
